import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, CheckCircle2, Send, RotateCcw, XCircle,
  Layers, AlertTriangle,
} from 'lucide-react';

import { usePermission }   from '@/hooks/usePermission';
import { Permissions }     from '@/constants/permissions';
import { AppRoutes }       from '@/constants/routes';
import { Spinner }         from '@/components/ui/Spinner';
import { cn }              from '@/utils/cn';
import { apiClient }       from '@/api/client';

// â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
interface IntegrationBatchLineDto {
  id: string;
  lineNumber: number;
  accountCode: string;
  accountName: string;
  description: string;
  debitAmount: number;
  creditAmount: number;
  costCenter?: string;
  location?: string;
  status: string;
  errorMessage?: string;
}

interface IntegrationBatchDetailDto {
  id: string;
  batchNumber: string;
  sourceModule: string;
  sourceReference: string;
  status: string;
  currencyCode: string;
  totalDebits: number;
  totalCredits: number;
  batchDate: string;
  postedDate?: string;
  errorMessage?: string;
  createdBy: string;
  createdDate: string;
  lines: IntegrationBatchLineDto[];
}

const STATUS_COLORS: Record<string, string> = {
  Pending:   'bg-amber-100 dark:bg-amber-900/30  text-amber-700 dark:text-amber-400',
  Validated: 'bg-blue-100 dark:bg-blue-900/30   text-blue-700 dark:text-blue-400',
  Posted:    'bg-teal-100 dark:bg-teal-900/30   text-teal-700 dark:text-teal-400',
  Failed:    'bg-red-100 dark:bg-red-900/30    text-red-700 dark:text-red-400',
  Cancelled: 'bg-slate-100 dark:bg-slate-800  text-slate-600 dark:text-gray-400',
  Retrying:  'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
};

const LINE_STATUS_COLORS: Record<string, string> = {
  OK:      'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400',
  Error:   'bg-red-100 dark:bg-red-900/30  text-red-700 dark:text-red-400',
  Warning: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
};

// â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const IntegrationBatchDetailPage = () => {
  const { id }            = useParams<{ id: string }>();
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();

  const canValidate = hasPermission(Permissions.INTEGRATION_POSTING_VALIDATE);
  const canPost     = hasPermission(Permissions.INTEGRATION_POSTING_POST);
  const canRetry    = hasPermission(Permissions.INTEGRATION_POSTING_RETRY);
  const canCancel   = hasPermission(Permissions.INTEGRATION_POSTING_CANCEL);

  const [batch,         setBatch]         = useState<IntegrationBatchDetailDto | null>(null);
  const [loading,       setLoading]       = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error,         setError]         = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      // The API is /integration-posting-batches. /integration/posting-batches is this screen's own
      // address in the browser, and asking the server for it is a 404.
      const res = await apiClient.get(`/integration-posting-batches/${id}`);
      setBatch(res.data?.data ?? null);
    } catch (err) {
      // A failed load must not read as an empty screen.
      setBatch(null);
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Could not load this batch.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (action: 'validate' | 'post' | 'retry' | 'cancel') => {
    if (!id) return;
    setError(null);
    setActionLoading(true);
    try {
      // Cancelling needs a reason; the endpoint refuses a request without a body.
      const body = action === 'cancel' ? { reason: 'Cancelled from console' } : undefined;
      await apiClient.post(`/integration-posting-batches/${id}/${action}`, body);
      load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? `Action "${action}" failed.`);
    } finally {
      setActionLoading(false);
    }
  };

  const fmtDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const fmtAmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2 });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-slate-50 dark:bg-slate-900">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-slate-50 dark:bg-slate-900 text-slate-400">
        <Layers size={48} className="mb-3 opacity-40" />
        {/* The server's reason when the load failed, so a fault does not read as "no such batch". */}
        <p role="alert" className="text-sm font-medium">{error ?? 'Batch not found'}</p>
        <button onClick={() => navigate(AppRoutes.INTEGRATION_BATCHES_LIST)}
          className="mt-3 text-teal-600 dark:text-teal-400 text-sm hover:underline">
          Back to list
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* â”€â”€ Header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(AppRoutes.INTEGRATION_BATCHES_LIST)}
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Layers size={20} className="text-teal-600 dark:text-teal-400" />
                Batch {batch.batchNumber}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                {batch.sourceModule} · {batch.sourceReference}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={cn('px-3 py-1 rounded-full text-sm font-medium', STATUS_COLORS[batch.status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
              {batch.status}
            </span>
            <button onClick={load}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors" title="Refresh">
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        {/* â”€â”€ Actions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="flex items-center gap-2 mt-3">
          {canValidate && batch.status === 'Pending' && (
            <button onClick={() => doAction('validate')} disabled={actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
              <CheckCircle2 size={14} /> Validate
            </button>
          )}
          {canPost && batch.status === 'Validated' && (
            <button onClick={() => doAction('post')} disabled={actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
              <Send size={14} /> Post
            </button>
          )}
          {canRetry && batch.status === 'Failed' && (
            <button onClick={() => doAction('retry')} disabled={actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
              <RotateCcw size={14} /> Retry
            </button>
          )}
          {canCancel && ['Pending', 'Validated', 'Failed'].includes(batch.status) && (
            <button onClick={() => doAction('cancel')} disabled={actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-red-600 dark:text-red-400 hover:bg-red-50 disabled:opacity-50 text-sm font-medium rounded-lg transition-colors">
              <XCircle size={14} /> Cancel
            </button>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            <AlertTriangle size={15} className="shrink-0" />
            {error}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-5">

        {/* â”€â”€ Batch summary cards â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Currency',    value: batch.currencyCode },
            { label: 'Batch Date',  value: fmtDate(batch.batchDate) },
            { label: 'Total Debits', value: fmtAmt(batch.totalDebits) },
            { label: 'Total Credits', value: fmtAmt(batch.totalCredits) },
          ].map(card => (
            <div key={card.label} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{card.label}</p>
              <p className="text-base font-semibold text-slate-900 dark:text-white font-mono">{card.value}</p>
            </div>
          ))}
        </div>

        {/* â”€â”€ Error banner â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        {batch.errorMessage && (
          <div className="flex items-start gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400 text-sm">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
            <div>
              <p className="font-medium mb-1">Processing Error</p>
              <p className="text-xs font-mono">{batch.errorMessage}</p>
            </div>
          </div>
        )}

        {/* â”€â”€ Lines table â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-800 dark:text-white">
              Batch Lines
              <span className="ml-2 text-sm font-normal text-slate-500 dark:text-gray-400">({batch.lines.length})</span>
            </h2>
          </div>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider w-12">#</th>
                  <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Account</th>
                  <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-right  text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Debit</th>
                  <th className="px-4 py-3 text-right  text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Credit</th>
                  <th className="px-4 py-3 text-left   text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Cost Center</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {batch.lines.map(line => (
                  <tr key={line.id} className={cn('transition-colors', line.status === 'Error' ? 'bg-red-50 dark:bg-red-900/10' : 'hover:bg-slate-50 dark:hover:bg-slate-700/40')}>
                    <td className="px-4 py-2.5 text-center text-slate-500 dark:text-slate-400 text-xs">{line.lineNumber}</td>
                    <td className="px-4 py-2.5">
                      <div className="font-mono font-semibold text-slate-800 dark:text-white text-xs">{line.accountCode}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[160px]">{line.accountName}</div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400 text-xs max-w-[200px] truncate">{line.description}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-900 dark:text-white text-xs">
                      {line.debitAmount > 0 ? fmtAmt(line.debitAmount) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-900 dark:text-white text-xs">
                      {line.creditAmount > 0 ? fmtAmt(line.creditAmount) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400 text-xs">{line.costCenter ?? '—'}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', LINE_STATUS_COLORS[line.status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
                        {line.status}
                      </span>
                      {line.errorMessage && (
                        <p className="text-xs text-red-500 mt-0.5 max-w-[180px] truncate" title={line.errorMessage}>{line.errorMessage}</p>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 dark:bg-slate-800/60 border-t-2 border-slate-300 dark:border-slate-600">
                <tr>
                  <td colSpan={3} className="px-4 py-3 text-right text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Totals</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-slate-900 dark:text-white text-sm">{fmtAmt(batch.totalDebits)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-slate-900 dark:text-white text-sm">{fmtAmt(batch.totalCredits)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* â”€â”€ Metadata â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Audit Information</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">Created By</p>
              <p className="font-medium text-slate-800 dark:text-white">{batch.createdBy}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">Created Date</p>
              <p className="font-medium text-slate-800 dark:text-white">{fmtDate(batch.createdDate)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">Posted Date</p>
              <p className="font-medium text-slate-800 dark:text-white">{fmtDate(batch.postedDate)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">Batch ID</p>
              <p className="font-mono text-xs text-slate-600 dark:text-slate-400 break-all">{batch.id}</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

